/**
 * Shared branded recipe-card HTML template used by:
 *   • Recipe Card modal Print + PDF (single recipe)
 *   • Recipe Center bulk booklet Print + PDF (many recipes + cover page)
 *
 * Every output — screen preview, print sheet, PDF, booklet — flows through
 * `buildBrandedHtml([{view, imageUrl, scaleLine}], opts)` so the pixels
 * always match.
 *
 * `view` is the recipe object: name, servings, prep_time_min, cook_time_min,
 * ingredients [{quantity, unit, item}], instructions [str], freeze_dry_prep,
 * rehydration, storage, dietary_tags [key], nutrition {...}.
 */

export const TAG_LABEL = {
  vegan: 'Vegan',
  vegetarian: 'Vegetarian',
  gluten_free: 'Gluten-Free',
  dairy_free: 'Dairy-Free',
  low_sodium: 'Low Sodium',
  low_sugar: 'Low Sugar',
  diabetic_friendly: 'Diabetic-Friendly',
  keto: 'Keto',
  paleo: 'Paleo',
  pescatarian: 'Pescatarian',
  nut_free: 'Nut-Free',
  high_protein: 'High Protein',
};

export function escapeHtml(s) {
  return String(s ?? '')
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
}

// Nutrition row: ALWAYS emit — user explicitly asked to see every field
// even when the AI returned zero or nothing, so operators can add missing
// values by hand later. Missing values render as an em-dash so the row
// stays readable on the printed card.
function nutriRow(label, value, unit, indent = false) {
  const cls = indent ? 'indent' : '';
  const inner = indent ? label : `<b>${label}</b>`;
  const display = value === null || value === undefined || value === ''
    ? '—'
    : `${value}${unit || ''}`;
  return `<tr class="${cls}"><td>${inner}</td><td>${display}</td></tr>`;
}

const STYLES = `
  @page { size: letter; margin: 12mm; }
  * { box-sizing: border-box; }
  body { font-family: "Helvetica Neue", Arial, sans-serif; color: #000; margin: 0; background: #fff; }
  .recipe-page { padding: 0; page-break-after: always; break-after: page; }
  .recipe-page:last-child { page-break-after: auto; break-after: auto; }
  .cover-page { padding: 40mm 20mm; text-align: center; page-break-after: always; break-after: page; }
  .cover-title { font-size: 32pt; font-weight: 900; letter-spacing: -0.5px; margin: 0; }
  .cover-title span { color: #10b981; }
  .cover-sub { font-size: 12pt; color: #444; margin: 12px 0 28px; }
  .cover-list { text-align: left; max-width: 480px; margin: 0 auto; font-size: 11pt; line-height: 1.9; }
  .cover-list li { border-bottom: 1px dotted #999; padding: 2px 0; display: flex; justify-content: space-between; }
  .cover-meta { margin-top: 40px; font-size: 9pt; color: #666; letter-spacing: 2px; text-transform: uppercase; }

  header { display: flex; align-items: center; justify-content: space-between; border-bottom: 3px solid #000; padding-bottom: 8px; margin-bottom: 12px; }
  .brand { font-weight: 900; font-size: 18pt; letter-spacing: -0.3px; }
  .brand span { color: #10b981; }
  .brand small { display: block; font-size: 8pt; font-weight: 600; letter-spacing: 3px; color: #555; text-transform: uppercase; }
  .headmeta { text-align: right; font-size: 9pt; color: #333; }

  .hero { display: grid; grid-template-columns: 1fr 200px; gap: 16px; align-items: start; }
  h1 { font-size: 22pt; margin: 0 0 4px; }
  .sub { font-size: 9pt; text-transform: uppercase; letter-spacing: 1px; color: #444; }
  .tags { margin-top: 6px; }
  .tag { display: inline-block; font-size: 8pt; font-weight: 700; text-transform: uppercase; letter-spacing: 1px; padding: 2px 8px; border: 1px solid #059669; color: #065f46; background: #d1fae5; border-radius: 999px; margin-right: 4px; }
  .photo { width: 200px; height: 200px; object-fit: cover; border: 2px solid #000; border-radius: 4px; background: #eee; display: block; }
  .photo-empty { color: #666; font-size: 9pt; text-align: center; padding: 40px 8px; }

  h2 { font-size: 12pt; text-transform: uppercase; letter-spacing: 1px; border-bottom: 1px solid #666; padding-bottom: 2px; margin: 14px 0 6px; }
  ul, ol { margin: 0; padding-left: 18px; font-size: 10pt; line-height: 1.55; }
  ul li, ol li { padding: 2px 0; }
  ul li { list-style: disc; }
  .q { font-family: "SFMono-Regular", Consolas, monospace; display: inline-block; min-width: 60px; font-weight: 700; }
  .info { font-size: 10pt; margin-top: 8px; line-height: 1.5; }
  .info b { text-transform: uppercase; font-size: 8pt; letter-spacing: 1px; color: #333; }

  .nf { width: 100%; margin-top: 12px; border-collapse: collapse; border: 2px solid #000; font-size: 9.5pt; }
  .nf caption { font-weight: 900; text-align: left; font-size: 12pt; padding-bottom: 4px; caption-side: top; border-bottom: 3px solid #000; }
  .nf td { border-bottom: 1px solid #ccc; padding: 6px 8px; line-height: 1.4; }
  .nf .cal td { border-bottom: 2px solid #000; padding: 8px 8px; }
  .nf .cal td:first-child { font-size: 11pt; }
  .nf .cal td:last-child { text-align: right; font-size: 14pt; font-weight: 900; }
  .nf td:last-child { text-align: right; font-family: "SFMono-Regular", monospace; }
  .nf tr.indent td:first-child { padding-left: 22px; color: #333; }
  .nf-note { font-size: 7.5pt; font-style: italic; color: #555; margin: 6px 0 0; }

  footer { border-top: 1px solid #000; margin-top: 22px; padding-top: 6px; display: flex; justify-content: space-between; font-size: 8pt; color: #555; }
`;

function ingredientsHtml(view) {
  if (!view.ingredients?.length) return '';
  const rows = view.ingredients.map((it) => {
    const qty = Number.isFinite(it.quantity) ? it.quantity : '';
    return `<li><span class="q">${qty} ${escapeHtml(it.unit || '')}</span> ${escapeHtml(it.item || '')}</li>`;
  }).join('');
  return `<h2>Ingredients</h2><ul>${rows}</ul>`;
}

function instructionsHtml(view) {
  if (!view.instructions?.length) return '';
  const rows = view.instructions.map((s) => `<li>${escapeHtml(s)}</li>`).join('');
  return `<h2>Instructions</h2><ol>${rows}</ol>`;
}

function nutritionHtml(view) {
  const n = view.nutrition || {};
  const hasAny = n.serving_size || n.calories || Object.values(n).some((v) => v !== null && v !== undefined && v !== '');
  if (!hasAny) return '';
  const cal = n.calories === null || n.calories === undefined || n.calories === ''
    ? '—' : escapeHtml(n.calories);
  return `
    <table class="nf">
      <caption>Nutrition Facts (per serving)</caption>
      ${n.serving_size ? `<tr><td colspan="2">Serving: ${escapeHtml(n.serving_size)}</td></tr>` : ''}
      <tr class="cal"><td><b>Calories</b></td><td>${cal}</td></tr>
      ${nutriRow('Total Fat',      n.total_fat_g,     'g')}
      ${nutriRow('Saturated Fat',  n.saturated_fat_g, 'g', true)}
      ${nutriRow('Trans Fat',      n.trans_fat_g,     'g', true)}
      ${nutriRow('Cholesterol',    n.cholesterol_mg,  'mg')}
      ${nutriRow('Sodium',         n.sodium_mg,       'mg')}
      ${nutriRow('Total Carbs',    n.total_carbs_g,   'g')}
      ${nutriRow('Dietary Fiber',  n.dietary_fiber_g, 'g', true)}
      ${nutriRow('Total Sugars',   n.total_sugars_g,  'g', true)}
      ${nutriRow('Added Sugars',   n.added_sugars_g,  'g', true)}
      ${nutriRow('Protein',        n.protein_g,       'g')}
    </table>
    <p class="nf-note">AI-estimated per-serving values. Missing rows (—) can be filled in from a certified lab or a verified reference before commercial labelling.</p>
  `;
}

function recipePageHtml(view, imageUrl, scaleLine = '') {
  const now = new Date().toLocaleDateString(undefined, {
    year: 'numeric', month: 'long', day: 'numeric',
  });
  const tags = (view.dietary_tags || []).map(
    (t) => `<span class="tag">${escapeHtml(TAG_LABEL[t] || t)}</span>`,
  ).join('');
  const infoBlock = (label, body) =>
    body ? `<div class="info"><b>${label}</b><br/>${escapeHtml(body)}</div>` : '';

  return `
<div class="recipe-page">
  <header>
    <div class="brand">
      <small>Freeze Dry <span>Academy</span></small>
      Recipe Card
    </div>
    <div class="headmeta">
      Printed ${now}<br/>
      ${escapeHtml((typeof window !== 'undefined' && window.location) ? window.location.origin : 'freezedry.academy')}
    </div>
  </header>
  <div class="hero">
    <div>
      <h1>${escapeHtml(view.name || 'Recipe')}</h1>
      <div class="sub">
        Yields ${escapeHtml(view.servings || '')} servings
        ${view.prep_time_min ? ` · Prep ${escapeHtml(view.prep_time_min)} min` : ''}
        ${view.cook_time_min ? ` · Cook ${escapeHtml(view.cook_time_min)} min` : ''}
        ${scaleLine || ''}
      </div>
      ${tags ? `<div class="tags">${tags}</div>` : ''}
    </div>
    ${imageUrl
      ? `<img class="photo" src="${imageUrl}" alt="${escapeHtml(view.name || '')}" crossorigin="anonymous" />`
      : '<div class="photo photo-empty">Photo generating — reprint after it appears in the Recipe Book.</div>'}
  </div>
  ${ingredientsHtml(view)}
  ${instructionsHtml(view)}
  ${infoBlock('Freeze-dry prep', view.freeze_dry_prep)}
  ${infoBlock('Rehydration',    view.rehydration)}
  ${infoBlock('Storage',        view.storage)}
  ${nutritionHtml(view)}
  <footer>
    <div>© ${new Date().getFullYear()} Freeze Dry Academy · Community Recipe Book</div>
    <div>freezedry.academy — recipe support tool, not a certified reference</div>
  </footer>
</div>`;
}

function coverPageHtml({ coverTitle, coverSubtitle, entries }) {
  const list = (entries || []).map(
    (e) => `<li><span>${escapeHtml(e.name)}</span><span>${escapeHtml(e.servings || '')} srv</span></li>`,
  ).join('');
  const printed = new Date().toLocaleDateString(undefined, {
    year: 'numeric', month: 'long', day: 'numeric',
  });
  return `
<div class="cover-page">
  <div class="brand" style="margin-bottom: 12px;">
    <small>Freeze Dry <span>Academy</span></small>
  </div>
  <h1 class="cover-title">${escapeHtml(coverTitle || 'Recipe Booklet')}</h1>
  <div class="cover-sub">${escapeHtml(coverSubtitle || 'Community Recipe Book export')}</div>
  <ol class="cover-list">${list}</ol>
  <div class="cover-meta">Printed ${printed} · ${entries.length} recipe${entries.length === 1 ? '' : 's'}</div>
</div>`;
}

/**
 * Add a (possibly very tall) html2canvas snapshot to a jsPDF document,
 * spilling any overflow onto additional pages so nothing gets clipped at
 * the letter-page bottom. Used by BOTH the single-recipe PDF export and
 * the multi-recipe booklet export so their overflow behaviour matches.
 *
 * jsPDF is already in the caller's bundle — this helper only needs the
 * open `pdf` instance and a canvas. `startNewPage` is true when the
 * caller wants to `pdf.addPage()` before drawing (used between booklet
 * entries so each recipe starts on its own fresh page).
 */
export function addCanvasAsPages(pdf, canvas, opts = {}) {
  const {
    pageWmm     = 215.9,   // Letter width in mm
    pageHmm     = 279.4,   // Letter height in mm
    startNewPage = false,
  } = opts;
  if (startNewPage) pdf.addPage('letter', 'portrait');

  const totalHmm = (canvas.height / canvas.width) * pageWmm;
  const dataUrl  = canvas.toDataURL('image/png');

  if (totalHmm <= pageHmm) {
    pdf.addImage(dataUrl, 'PNG', 0, 0, pageWmm, totalHmm);
    return;
  }

  // Tall canvas — draw it at Y=0 on the first page, then repeatedly on
  // new pages with a negative Y so PDF page bounds clip the overflow.
  // This is the standard jsPDF "poor man's" pagination trick.
  let heightLeft = totalHmm;
  let position   = 0;
  pdf.addImage(dataUrl, 'PNG', 0, position, pageWmm, totalHmm);
  heightLeft -= pageHmm;
  while (heightLeft > 0) {
    position = heightLeft - totalHmm; // negative, pushes image up
    pdf.addPage('letter', 'portrait');
    pdf.addImage(dataUrl, 'PNG', 0, position, pageWmm, totalHmm);
    heightLeft -= pageHmm;
  }
}

export function buildBrandedHtml(pages, opts = {}) {
  const { withPrintScript = false, cover = null } = opts;
  const printScript = withPrintScript ? `
<script>
  window.addEventListener('load', () => setTimeout(() => { window.print(); }, 300));
  window.addEventListener('afterprint', () => window.close());
</script>` : '';

  const coverHtml = cover
    ? coverPageHtml({
        coverTitle:    cover.coverTitle,
        coverSubtitle: cover.coverSubtitle,
        entries: pages.map((p) => ({
          name: p.view?.name || '',
          servings: p.view?.servings || '',
        })),
      })
    : '';

  const body = pages
    .map((p) => recipePageHtml(p.view, p.imageUrl, p.scaleLine))
    .join('\n');

  return `<!DOCTYPE html>
<html lang="en"><head><meta charset="utf-8">
<title>${escapeHtml(cover?.coverTitle || pages[0]?.view?.name || 'Recipe')} — Freeze Dry Academy</title>
<style>${STYLES}</style>
</head>
<body>
${coverHtml}
${body}
${printScript}
</body></html>`;
}
